import React, { useEffect, useState } from "react";
import {
  FaPhone,
  FaCalendar,
  FaExclamationCircle,
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaEye,
  FaUser,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import IssueDetailModal from "../components/IssueDetailModal";
import IssueStatusUpdateModal from "../components/IssueStatusUpdateModal";

const MyAssignedIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  // Modals
  const [detailModal, setDetailModal] = useState({ show: false, issue: null });
  const [statusModal, setStatusModal] = useState({ show: false, issue: null });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
  });

  // Fetch assigned issues
  const fetchMyIssues = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const queryParams = statusFilter
        ? `?status=${encodeURIComponent(statusFilter)}`
        : "";

      const response = await axiosInstance.get(
        `${API.ADMIN.BASE}/issues/my-assigned${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fetchedIssues = response.data.data.issues || [];
      setIssues(fetchedIssues);

      // Calculate statistics
      setStats({
        total: fetchedIssues.length,
        pending: fetchedIssues.filter((issue) => issue.status === "Pending")
          .length,
        inProgress: fetchedIssues.filter(
          (issue) => issue.status === "In progress"
        ).length,
        resolved: fetchedIssues.filter((issue) => issue.status === "Resolved")
          .length,
      });
    } catch (error) {
      console.error("Error fetching assigned issues:", error);
      toast.error("Failed to fetch assigned issues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyIssues();
  }, [statusFilter]);

  // Handle status update
  const handleStatusUpdate = async (issueId, statusData) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axiosInstance.put(
        `${API.ADMIN.BASE}/issues/${issueId}/status`,
        statusData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Issue status updated successfully!");
      setStatusModal({ show: false, issue: null });
      fetchMyIssues(); // Refresh the list
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "In progress":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "Resolved":
        return "text-green-600 bg-green-100 border-green-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  // Get priority color based on issue type
  const getPriorityColor = (issueType) => {
    const highPriority = ["Payment Problems", "Login Issues"];
    return highPriority.includes(issueType)
      ? "text-red-600"
      : "text-orange-600";
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          My Assigned Issues
        </h1>
        <p className="text-gray-600">Issues assigned to you for resolution</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FaUser className="text-blue-500 text-2xl mr-3" />
            <div>
              <p className="text-gray-500 text-sm">Total Assigned</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FaExclamationCircle className="text-yellow-500 text-2xl mr-3" />
            <div>
              <p className="text-gray-500 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pending}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FaClock className="text-blue-500 text-2xl mr-3" />
            <div>
              <p className="text-gray-500 text-sm">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.inProgress}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 text-2xl mr-3" />
            <div>
              <p className="text-gray-500 text-sm">Resolved</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.resolved}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">
            Filter by Status:
          </label>
          <select
            className="border rounded-lg px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="In progress">In Progress</option>
            <option value="Resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Issues List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading assigned issues...</span>
          </div>
        ) : issues.length === 0 ? (
          <div className="text-center py-12">
            <FaUser className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">
              No issues assigned to you
            </p>
            <p className="text-gray-400 text-sm">
              {statusFilter
                ? `No ${statusFilter.toLowerCase()} issues found`
                : "Check back later for new assignments"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {issues.map((issue) => (
              <div
                key={issue._id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Issue Header */}
                    <div className="flex items-center mb-2">
                      <FaExclamationCircle
                        className={`${getPriorityColor(issue.issueType)} mr-2`}
                      />
                      <h3 className="text-lg font-medium text-gray-900">
                        {issue.title}
                      </h3>
                      <span
                        className={`ml-3 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                          issue.status
                        )}`}
                      >
                        {issue.status}
                      </span>
                    </div>

                    {/* Issue Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Issue Type</p>
                        <p className="text-sm font-medium text-gray-900">
                          {issue.issueType}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <FaPhone className="text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="text-sm font-medium text-gray-900">
                            {issue.phone} ({issue.role})
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Issue Description */}
                    <div className="mb-3">
                      <p className="text-sm text-gray-700">{issue.message}</p>
                      {issue.additionalMessage && (
                        <p className="text-sm text-blue-700 mt-1 italic">
                          "{issue.additionalMessage}"
                        </p>
                      )}
                    </div>

                    {/* Assignment Details */}
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <div className="flex items-center">
                        <FaCalendar className="mr-1" />
                        <span>
                          Created:{" "}
                          {new Date(issue.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {issue.assignedAt && (
                        <div className="flex items-center">
                          <FaUser className="mr-1" />
                          <span>
                            Assigned:{" "}
                            {new Date(issue.assignedAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {issue.assignedBy && (
                        <span>by {issue.assignedBy.fullName}</span>
                      )}
                    </div>

                    {/* Resolution Info */}
                    {issue.status === "Resolved" && issue.resolvedAt && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-700">
                          <FaCheckCircle className="inline mr-1" />
                          Resolved on{" "}
                          {new Date(issue.resolvedAt).toLocaleDateString()}
                        </p>
                        {issue.resolutionNotes && (
                          <p className="text-sm text-green-600 mt-1">
                            {issue.resolutionNotes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => setDetailModal({ show: true, issue })}
                      className="text-blue-600 hover:text-blue-800 p-2"
                      title="View Details"
                    >
                      <FaEye />
                    </button>

                    <button
                      onClick={() => setStatusModal({ show: true, issue })}
                      className="text-green-600 hover:text-green-800 p-2"
                      title="Update Status"
                    >
                      <FaEdit />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions Guide */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Quick Actions Guide</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-blue-700">
          <div>
            <FaEye className="inline mr-1" />
            <strong>View:</strong> See full issue details and customer
            information
          </div>
          <div>
            <FaEdit className="inline mr-1" />
            <strong>Update:</strong> Change status and add resolution notes
          </div>
          <div>
            <FaPhone className="inline mr-1" />
            <strong>Contact:</strong> Always record customer interaction details
          </div>
        </div>
      </div>

      {/* Modals */}
      <IssueDetailModal
        show={detailModal.show}
        issue={detailModal.issue}
        onClose={() => setDetailModal({ show: false, issue: null })}
      />

      <IssueStatusUpdateModal
        show={statusModal.show}
        issue={statusModal.issue}
        onClose={() => setStatusModal({ show: false, issue: null })}
        onUpdate={handleStatusUpdate}
      />
    </div>
  );
};

export default MyAssignedIssues;
